import React, { useState } from 'react';
import { ModalController } from '../../../components/Modal/ControlledModal';
import { Form, useChanges } from '../../../components/Form/Form';
import { TagsInput } from '../../../components/Form/TagsInput/TagsInput';
import { Order, updateOrders } from '../../../model/order';
import { intersection, uniq, difference, flatten } from 'lodash';
import { handleError } from '../../../model/notification';
import { Stack } from '../../../components/Stack/Stack';

export interface TagsUpdateModel {
  tags: string[];
  uniqueTags: string[];
}

interface Props {
  orders: Order[];
  controller: ModalController<TagsUpdateModel>;
  onUpdate: () => void;
}

export function TagsUpdate({ orders, controller, onUpdate }: Props): JSX.Element {
  function getSharedTags(): string[] {
    const ordersTags = orders.map((order) => order.tags);
    return intersection(...ordersTags);
  }
  function getUniqueTags(): string[] {
    const ordersTags = uniq(flatten(orders.map((order) => order.tags)));
    const sharedTags = getSharedTags();

    return difference(ordersTags, sharedTags);
  }

  const [, , model, trackChanges] = useChanges<TagsUpdateModel>({
    tags: getSharedTags(),
    uniqueTags: getUniqueTags(),
  });
  const [deleteTags, setDeleteTags] = useState<string[]>([]);

  async function handleOnSubmit(): Promise<void> {
    const ordersToUpdate: Order[] = [];

    orders.forEach((order) => {
      ordersToUpdate.push({
        ...order,
        tags: uniq([...difference(order.tags, deleteTags), ...model.tags]),
      });
    });

    try {
      await updateOrders(ordersToUpdate);
      controller.close();
      onUpdate();
    } catch (error) {
      handleError(error);
    }
  }

  return (
    <Form model={model} onInput={trackChanges}>
      {(form) => {
        controller.configure({
          onSubmit: handleOnSubmit,
          canSubmit: form.valid,
        });
        return (
          <Stack>
            <TagsInput
              label="Tags in common"
              description="Add or remove tags for all selected orders"
              name="tags"
              onTagDelete={(tag) => setDeleteTags([...deleteTags, tag])}
              required
            />
            <TagsInput
              label="All other tags on selected orders"
              description="You can only remove tags here. Keep in mind though that you can’t reverse it after saving"
              name="uniqueTags"
              required
              onTagDelete={(tag) => setDeleteTags([...deleteTags, tag])}
              disabled
            />
          </Stack>
        );
      }}
    </Form>
  );
}
